import React, { useCallback, useEffect, useState } from 'react';
import { CheckCircle2, XCircle, MinusCircle, ExternalLink, Plus } from 'lucide-react';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import { workspaceAPI, coachDailyContactsAPI, serviceSopAPI, serviceReportsAPI } from '../../../services/api';
import { resolvePhotoUrl } from '../../../utils/photoUtils';
import {
  fmtMD, fmtMDHM, asArray, purposeLabel, methodLabel, outcomeLabel, REPORT_STATUS, reportStatusKey, monthNo, fromLocalInput,
} from '../../service/sopShared';
import { laNowInput } from '../../../utils/laTime';

// 整改单 default due: the day after tomorrow, 18:00 Pacific.
const rectDefaultDue = () => `${laNowInput(2 * 86400000).slice(0, 10)}T18:00`;
import { S, card, btnPrimary, btnGhost, btnLink, field, sectionTitle, NotDeployed, is404, TabIntro } from './wsShared';
import { TAB_EXPLAIN } from '../../tour/tourSteps';

// ⑤ 审核 (主管 roles only). Three steps, top to bottom:
//   ① KPI 检查清单 — is this coach meeting the 5.0 standards for this member
//   ② 待审核的记录 — contacts, frequency changes, monthly reports: 通过 / 退回
//   ③ 整改单 — when something is not up to standard: what, who, by when.
//
// Props: profileId, isZh, svc (service-status), contacts (activity contact
// rows), teachers [{id,name}], defaultCoachId, rectFallback (array from
// /workspace, used when the list endpoint is missing), onOpenReport(id),
// onChanged (refresh the workspace + list).

const Section = ({ n, title, hint, children, actions, tour }) => (
  <section data-tour={tour} style={{ ...card, padding: '16px 18px', marginBottom: 16 }}>
    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, marginBottom: 12, flexWrap: 'wrap' }}>
      <span style={{ width: 24, height: 24, borderRadius: '50%', background: '#111111', color: '#FFFDF9', fontSize: 12.5, fontWeight: 500, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flex: '0 0 auto' }}>{n}</span>
      <div style={{ flex: 1, minWidth: 0 }}>
        <h3 style={{ ...sectionTitle, margin: 0 }}>{title}</h3>
        {hint && <div style={{ fontSize: 12.5, color: S.sub, marginTop: 2, lineHeight: 1.6 }}>{hint}</div>}
      </div>
      {actions}
    </div>
    {children}
  </section>
);

const Sub = ({ title, count, children, tour }) => (
  <div data-tour={tour} style={{ marginTop: 10 }}>
    <div style={{ fontSize: 13, fontWeight: 500, color: S.ink, marginBottom: 6 }}>{title}{count != null ? <span style={{ color: count ? '#9E2B25' : S.mute, marginLeft: 6 }}>{count}</span> : null}</div>
    {children}
  </div>
);

const RECT_STATUS = {
  open: ['待整改', '#9E2B25', '#F3E3E1'],
  done: ['老师已完成 · 待你核实', '#93591A', '#F3EADB'],
  verified: ['已核实', '#3F7D5A', '#E6EEE8'],
};

export default function ReviewTab({ profileId, isZh = true, svc, contacts = [], teachers = [], defaultCoachId, rectFallback, presetRect, onOpenReport, onChanged }) {
  const t = (zh, en) => (isZh ? zh : en);
  const [kpi, setKpi] = useState(null);           // { items } | 'missing'
  const [extraContacts, setExtraContacts] = useState([]);
  const [reports, setReports] = useState([]);
  const [rects, setRects] = useState(null);       // array | 'missing'
  const [busy, setBusy] = useState(null);
  const [rejectFor, setRejectFor] = useState(null);
  const [rejectNote, setRejectNote] = useState('');
  const [newRect, setNewRect] = useState(null);   // { title, coach_user_id, due_at }

  const load = useCallback(async () => {
    const [k, ex, rp, rc] = await Promise.all([
      workspaceAPI.kpiCheck(profileId).then((r) => r.data).catch((e) => (is404(e) ? 'missing' : null)),
      coachDailyContactsAPI.listAll({ profile_id: profileId, review_status: 'submitted', date_from: '2020-01-01', limit: 100 })
        .then((r) => (Array.isArray(r.data?.data) ? r.data.data : Array.isArray(r.data?.items) ? r.data.items : Array.isArray(r.data) ? r.data : []))
        .catch(() => []),
      serviceReportsAPI.forProfile(profileId).then((r) => (Array.isArray(r.data?.reports) ? r.data.reports : Array.isArray(r.data) ? r.data : [])).catch(() => []),
      workspaceAPI.listRectifications(profileId)
        .then((r) => (Array.isArray(r.data?.items) ? r.data.items : Array.isArray(r.data?.rectifications) ? r.data.rectifications : Array.isArray(r.data) ? r.data : []))
        .catch((e) => (is404(e) ? 'missing' : [])),
    ]);
    setKpi(k);
    setExtraContacts(ex);
    setReports(rp);
    setRects(rc);
  }, [profileId]);
  useEffect(() => { load(); }, [load]);
  // 下一步 「14 天无推荐 → 主管介入」 opens the 新建整改单 form prefilled.
  useEffect(() => {
    if (!presetRect || !presetRect.n) return;
    setNewRect({ title: presetRect.title || '', coach_user_id: defaultCoachId ? String(defaultCoachId) : '', due_at: rectDefaultDue() });
  }, [presetRect]);

  const after = async () => { await load(); onChanged && onChanged(); };

  // 待审核联系记录 — from the activity feed (review_status) plus the admin list
  // where this viewer may read it.
  const pending = (() => {
    const m = new Map();
    contacts.filter((c) => c.review_status === 'submitted').forEach((c) => m.set(Number(c.id), {
      id: c.id, text: c.text, by: c.by, at: c.contacted_at || c.at, purpose: c.purpose, method: c.method, outcome: c.outcome, images: asArray(c.images),
    }));
    extraContacts.forEach((c) => {
      if (m.has(Number(c.id))) return;
      m.set(Number(c.id), { id: c.id, text: c.notes, by: c.coach_name, at: c.contacted_at || c.created_at, purpose: c.purpose, method: c.method, outcome: c.outcome, images: asArray(c.images) });
    });
    return [...m.values()].sort((a, b) => new Date(b.at || 0) - new Date(a.at || 0));
  })();

  const reviewContact = async (c, decision) => {
    const note = decision === 'rejected' ? rejectNote.trim() : '';
    if (decision === 'rejected' && !note) { toast.error(t('退回要写原因，老师才知道怎么改', 'Give a reason')); return; }
    setBusy(`c${c.id}`);
    try {
      try {
        await workspaceAPI.reviewContact(c.id, { decision, note: note || undefined });
      } catch (e) {
        if (!is404(e)) throw e;
        await (decision === 'approved' ? coachDailyContactsAPI.approve(c.id, note || null) : coachDailyContactsAPI.reject(c.id, note));
      }
      toast.success(decision === 'approved' ? t('已通过', 'Approved') : t('已退回', 'Returned'));
      setRejectFor(null); setRejectNote('');
      await after();
    } catch (e) {
      toast.error(e?.response?.data?.error || t('操作失败', 'Failed'));
    } finally { setBusy(null); }
  };

  const reviewInterval = async (action) => {
    const req = svc && svc.pending_interval_request;
    if (!req) return;
    let note = '';
    if (action === 'reject') {
      // eslint-disable-next-line no-alert
      const v = window.prompt(t('驳回原因', 'Reason'), '');
      if (v === null) return;
      note = v.trim();
    }
    setBusy('interval');
    try {
      await serviceSopAPI.reviewInterval(req.id, { action, note: note || undefined });
      toast.success(action === 'approve' ? t('已确认，频率已生效', 'Approved') : t('已驳回', 'Rejected'));
      await after();
    } catch (e) {
      toast.error(e?.response?.data?.error || t('操作失败', 'Failed'));
    } finally { setBusy(null); }
  };

  const createRect = async () => {
    if (!newRect) return;
    if (!newRect.title.trim()) { toast.error(t('写清楚要做什么', 'Say what needs doing')); return; }
    const dueAt = newRect.due_at ? fromLocalInput(newRect.due_at) : null;   // typed as Pacific
    if (!dueAt) { toast.error(t('选一个截止日期', 'Pick a due date')); return; }
    setBusy('rect');
    try {
      await workspaceAPI.createRectification(profileId, {
        title: newRect.title.trim(),
        due_at: dueAt.toISOString(),
        coach_user_id: newRect.coach_user_id ? Number(newRect.coach_user_id) : undefined,
      });
      toast.success(t('整改单已发给老师', 'Sent to the coach'));
      setNewRect(null);
      await after();
    } catch (e) {
      toast.error(is404(e) ? t('整改单部署后可用', 'Available after the next deploy') : (e?.response?.data?.error || t('操作失败', 'Failed')));
    } finally { setBusy(null); }
  };

  const verifyRect = async (r, action = 'verify') => {
    // eslint-disable-next-line no-alert
    const v = window.prompt(action === 'reopen' ? t('退回重做的原因（必填）', 'Why (required)') : t('核实意见（选填）', 'Note (optional)'), '');
    if (v === null) return;
    if (action === 'reopen' && !v.trim()) { toast.error(t('请写原因', 'Give a reason')); return; }
    setBusy(`r${r.id}`);
    try {
      await workspaceAPI.updateRectification(r.id, { action, note: v.trim() || undefined });
      toast.success(action === 'reopen' ? t('已退回给老师', 'Sent back') : t('已核实', 'Verified'));
      await after();
    } catch (e) {
      toast.error(e?.response?.data?.error || t('操作失败', 'Failed'));
    } finally { setBusy(null); }
  };

  const req = svc && svc.pending_interval_request;
  const reviewReports = reports.filter((r) => ['submitted', 'returned', 'draft'].includes(r.status) || reportStatusKey(r) === 'overdue');
  const rectList = rects === 'missing' ? (Array.isArray(rectFallback) ? rectFallback : null) : rects;
  const defaultDue = rectDefaultDue;

  return (
    <div>
      <TabIntro isZh={isZh} explain={isZh ? TAB_EXPLAIN.review : ''}
        text={t('主管审核这位会员的服务：先看清单，再处理记录，不达标就开整改单。', 'Review this member’s service: checklist, records, corrective actions.')} />
      <div style={{ ...card, padding: '14px 18px', marginBottom: 16, background: '#EFEAE2' }}>
        <div style={{ fontSize: 14, fontWeight: 500, color: S.ink, marginBottom: 6 }}>{t('怎么审核', 'How to review')}</div>
        <ol style={{ margin: 0, paddingLeft: 20, fontSize: 13.5, color: S.ink, lineHeight: 1.85 }}>
          <li>{t('看下面的 KPI 检查清单：✗ 就是没做到。', 'Read the KPI checklist: ✗ means not met.')}</li>
          <li>{t('通过或退回待审核的记录（退回要写原因）。', 'Approve or return the records waiting for you (say why when returning).')}</li>
          <li>{t('不达标就开整改单：写清楚做什么、谁做、哪天前。不能只说“多关心一下会员”。', 'Not up to standard → open a 整改单: what, who, by when.')}</li>
        </ol>
      </div>

      <Section n={1} tour="ws-review-kpi" title={t('KPI 检查清单', 'KPI checklist')} hint={t('系统按 5.0 标准检查这位老师在这个会员身上的工作。', 'Checked against the 5.0 standard for this member.')}>
        {kpi === 'missing' ? <NotDeployed isZh={isZh} what={t('KPI 检查清单', 'The checklist')} />
          : !kpi ? <div style={{ color: S.mute, fontSize: 13 }}>{t('加载中…', 'Loading…')}</div>
            : asArray(kpi.items).length ? (
              <div style={{ display: 'flex', flexDirection: 'column' }}>
                {asArray(kpi.items).map((it) => {
                  const Icon = it.ok === true ? CheckCircle2 : it.ok === false ? XCircle : MinusCircle;
                  const color = it.ok === true ? S.ok : it.ok === false ? S.bad : S.mute;
                  return (
                    <div key={it.key} style={{ display: 'flex', gap: 10, alignItems: 'flex-start', padding: '8px 0', borderBottom: `1px solid ${S.line}` }}>
                      <Icon size={18} color={color} style={{ flex: '0 0 auto', marginTop: 1 }} />
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 14, color: S.ink, fontWeight: 500 }}>{it.label}</div>
                        {(it.detail || it.den != null) && <div style={{ fontSize: 12.5, color: S.sub, marginTop: 1 }}>{[it.den != null ? `${it.num != null ? it.num : 0} / ${it.den}` : '', it.detail].filter(Boolean).join(' · ')}</div>}
                      </div>
                      <span style={{ fontSize: 12, fontWeight: 500, color }}>{it.ok === true ? t('达标', 'Met') : it.ok === false ? t('未达标', 'Not met') : t('暂无数据', 'n/a')}</span>
                    </div>
                  );
                })}
              </div>
            ) : <div style={{ color: S.mute, fontSize: 13 }}>{t('暂无检查项', 'Nothing to check yet')}</div>}
      </Section>

      <Section n={2} title={t('待审核的记录', 'Waiting for your review')} hint={t('联系记录、联系频率调整、月度报告，逐条通过或退回。', 'Contacts, frequency changes and monthly reports.')}>
        <Sub tour="ws-review-pending" title={t('联系记录', 'Contact records')} count={pending.length}>
          {pending.length ? pending.map((c) => (
            <div key={c.id} style={{ padding: '10px 0', borderBottom: `1px solid ${S.line}` }}>
              <div style={{ fontSize: 12.5, color: S.sub }}>
                {[c.purpose ? purposeLabel(c.purpose, isZh) : '', c.method ? methodLabel(c.method, isZh) : '', c.outcome ? outcomeLabel(c.outcome, isZh) : '', c.by, fmtMDHM(c.at)].filter(Boolean).join(' · ')}
              </div>
              <div style={{ fontSize: 14, color: S.ink, marginTop: 3, lineHeight: 1.6, overflowWrap: 'anywhere' }}>{c.text || '—'}</div>
              {c.images.length > 0 && (
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 6 }}>
                  {c.images.map((u, i) => (
                    <a key={i} href={resolvePhotoUrl(u) || u} target="_blank" rel="noreferrer">
                      <img src={resolvePhotoUrl(u) || u} alt="" style={{ width: 52, height: 68, objectFit: 'cover', borderRadius: 2, border: `1px solid ${S.line}` }} />
                    </a>
                  ))}
                </div>
              )}
              {rejectFor === c.id ? (
                <div style={{ display: 'flex', gap: 8, marginTop: 8, flexWrap: 'wrap' }}>
                  <input value={rejectNote} autoFocus onChange={(e) => setRejectNote(e.target.value)} maxLength={500}
                    placeholder={t('退回原因：例如没有写会员反馈和下一步', 'Reason')} style={{ ...field, flex: '1 1 240px' }} />
                  <button type="button" onClick={() => { setRejectFor(null); setRejectNote(''); }} style={btnGhost}>{t('取消', 'Cancel')}</button>
                  <button type="button" disabled={busy === `c${c.id}`} onClick={() => reviewContact(c, 'rejected')} style={{ ...btnPrimary(busy === `c${c.id}`), background: S.bad }}>{t('确认退回', 'Return')}</button>
                </div>
              ) : (
                <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                  <button type="button" disabled={busy === `c${c.id}`} onClick={() => reviewContact(c, 'approved')} style={btnPrimary(busy === `c${c.id}`)}>{t('通过', 'Approve')}</button>
                  <button type="button" onClick={() => { setRejectFor(c.id); setRejectNote(''); }} style={btnGhost}>{t('退回', 'Return')}</button>
                </div>
              )}
            </div>
          )) : <div style={{ fontSize: 13, color: S.mute }}>{t('没有待审核的联系记录', 'Nothing waiting')}</div>}
        </Sub>

        <Sub title={t('联系频率调整申请', 'Frequency change')} count={req ? 1 : 0}>
          {req ? (
            <div style={{ padding: '8px 0' }}>
              <div style={{ fontSize: 14, color: S.ink }}>
                {req.pause_until ? t(`暂停联系到 ${fmtMD(req.pause_until)}`, `Pause until ${fmtMD(req.pause_until)}`)
                  : Number(req.interval_hours) === 168 ? t('改为每周联系一次', 'Weekly contact') : t(`改为每 ${req.interval_hours} 小时联系`, `Every ${req.interval_hours}h`)}
              </div>
              {req.reason && <div style={{ fontSize: 13, color: S.sub, marginTop: 2 }}>「{req.reason}」</div>}
              <div style={{ fontSize: 12, color: S.mute }}>{[req.requested_by_name, fmtMDHM(req.created_at)].filter(Boolean).join(' · ')}</div>
              <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                <button type="button" disabled={busy === 'interval'} onClick={() => reviewInterval('approve')} style={btnPrimary(busy === 'interval')}>{t('确认', 'Approve')}</button>
                <button type="button" disabled={busy === 'interval'} onClick={() => reviewInterval('reject')} style={btnGhost}>{t('驳回', 'Reject')}</button>
              </div>
            </div>
          ) : <div style={{ fontSize: 13, color: S.mute }}>{t('没有待确认的申请', 'Nothing waiting')}</div>}
        </Sub>

        <Sub title={t('月度服务报告', 'Monthly reports')} count={reviewReports.filter((r) => r.status === 'submitted').length}>
          {reviewReports.length ? reviewReports.map((r) => {
            const rk = reportStatusKey(r);
            const rs = REPORT_STATUS[rk] || REPORT_STATUS.draft;
            const mon = monthNo(r);
            return (
              <div key={r.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 0', borderBottom: `1px solid ${S.line}`, flexWrap: 'wrap' }}>
                <span style={{ fontSize: 14, color: S.ink, flex: 1, minWidth: 0 }}>
                  {mon ? t(`${mon} 月报告`, `Month ${mon}`) : t(`第 ${r.period_index} 期`, `Period ${r.period_index}`)}
                  <span style={{ marginLeft: 8, fontSize: 12, fontWeight: 500, color: rs.color, background: rs.bg, borderRadius: 2, padding: '2px 8px' }}>{isZh ? rs.zh : rs.en}</span>
                </span>
                <button type="button" onClick={() => onOpenReport && onOpenReport(r.id)} style={btnGhost}>{t('打开报告', 'Open')}</button>
                {r.status === 'submitted' && (
                  <Link to="/service-reports" style={{ ...btnPrimary(false), textDecoration: 'none' }}>
                    <ExternalLink size={13} />{t('去审核页', 'Review page')}
                  </Link>
                )}
              </div>
            );
          }) : <div style={{ fontSize: 13, color: S.mute }}>{t('没有要处理的报告', 'Nothing waiting')}</div>}
        </Sub>
      </Section>

      <Section n={3} tour="ws-review-rect" title={t('整改单', 'Corrective actions')}
        hint={t('不达标时开给老师：写清楚做什么、谁做、哪天前。老师在「下一步」里看到并标记完成，你再核实。', 'What, who, by when. The coach sees it in 下一步, marks it done, you verify.')}
        actions={!newRect && (
          <button type="button" onClick={() => setNewRect({ title: '', coach_user_id: defaultCoachId ? String(defaultCoachId) : '', due_at: defaultDue() })} style={btnPrimary(false)}>
            <Plus size={14} />{t('新建整改单', 'New')}
          </button>
        )}>
        {newRect && (
          <div style={{ padding: 12, borderRadius: 2, background: S.soft, display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 12 }}>
            <label style={{ fontSize: 12.5, color: S.sub, display: 'flex', flexDirection: 'column', gap: 4 }}>
              {t('要做什么（具体）', 'What exactly')}
              <input value={newRect.title} maxLength={300} autoFocus onChange={(e) => setNewRect((x) => ({ ...x, title: e.target.value }))}
                placeholder={t('例如：今天内补打 48 小时电话，并写清会员反馈和下一步', 'e.g. make the overdue call today and log feedback + next step')} style={field} />
            </label>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 8 }}>
              <label style={{ fontSize: 12.5, color: S.sub, display: 'flex', flexDirection: 'column', gap: 4 }}>
                {t('负责老师', 'Coach')}
                <select value={newRect.coach_user_id} onChange={(e) => setNewRect((x) => ({ ...x, coach_user_id: e.target.value }))} style={field}>
                  <option value="">{t('会员的主服务老师', 'The member’s coach')}</option>
                  {teachers.map((v) => <option key={v.id} value={String(v.id)}>{v.name}</option>)}
                </select>
              </label>
              <label style={{ fontSize: 12.5, color: S.sub, display: 'flex', flexDirection: 'column', gap: 4 }}>
                {t('截止时间', 'Due')}
                <input type="datetime-local" value={newRect.due_at} onChange={(e) => setNewRect((x) => ({ ...x, due_at: e.target.value }))} style={field} />
                <span style={{ fontSize: 11, fontWeight: 500, color: S.mute }}>{t('美西时间', 'PT')}</span>
              </label>
            </div>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button type="button" onClick={() => setNewRect(null)} style={btnGhost}>{t('取消', 'Cancel')}</button>
              <button type="button" disabled={busy === 'rect'} onClick={createRect} style={btnPrimary(busy === 'rect')}>{t('发给老师', 'Send')}</button>
            </div>
          </div>
        )}
        {rectList === null ? <NotDeployed isZh={isZh} what={t('整改单', 'Corrective actions')} />
          : rectList.length ? rectList.map((r) => {
            const st = RECT_STATUS[r.status] || RECT_STATUS.open;
            const late = r.status === 'open' && (r.overdue || (r.due_at && new Date(r.due_at).getTime() < Date.now()));
            return (
              <div key={r.id} style={{ padding: '10px 0', borderBottom: `1px solid ${S.line}`, display: 'flex', gap: 10, alignItems: 'flex-start', flexWrap: 'wrap' }}>
                <div style={{ flex: '1 1 260px', minWidth: 0 }}>
                  <div style={{ fontSize: 14, color: S.ink, fontWeight: 500, overflowWrap: 'anywhere' }}>{r.title}</div>
                  <div style={{ fontSize: 12, color: late ? S.bad : S.mute, marginTop: 2 }}>
                    {[r.coach_name, r.due_at ? t(`${fmtMDHM(r.due_at)} 前`, `by ${fmtMDHM(r.due_at)}`) : '', late ? t('已逾期', 'overdue') : '', r.created_by_name ? t(`开单：${r.created_by_name}`, `by ${r.created_by_name}`) : ''].filter(Boolean).join(' · ')}
                  </div>
                  {r.done_note && <div style={{ fontSize: 13, color: S.sub, marginTop: 3 }}>{t('老师说明：', 'Coach: ')}{r.done_note}</div>}
                </div>
                <span style={{ fontSize: 12, fontWeight: 500, color: st[1], background: st[2], borderRadius: 2, padding: '3px 10px' }}>{st[0]}</span>
                {r.status === 'done' && (
                  <>
                    <button type="button" disabled={busy === `r${r.id}`} onClick={() => verifyRect(r)} style={btnPrimary(busy === `r${r.id}`)}>{t('核实通过', 'Verify')}</button>
                    <button type="button" disabled={busy === `r${r.id}`} onClick={() => verifyRect(r, 'reopen')} style={btnGhost}>{t('退回重做', 'Reopen')}</button>
                  </>
                )}
              </div>
            );
          }) : <div style={{ fontSize: 13, color: S.mute }}>{t('没有整改单。都达标就不用开。', 'None open.')}</div>}
        {rects === 'missing' && Array.isArray(rectFallback) && <div style={{ marginTop: 8 }}><button type="button" onClick={load} style={btnLink}>{t('刷新', 'Refresh')}</button></div>}
      </Section>
    </div>
  );
}
